import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { NumericFormat } from "react-number-format"
import { useRemoteClientGet, useRemoteClientPost, useRemoteClientPut } from "../../../../configs/http-client"
import { CatchMessageError } from "../../../../remote/catch-message-error"
import { useLocalStorage } from "../../../../remote/local-storage"
import { Button } from "../../../components/ui/button"
import { DatePickerWithRange } from "../../../components/ui/date-range-picker"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../components/ui/form"
import { Input } from "../../../components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { useToast } from "../../../hooks"
import { type ExerciseSheetForm, exerciseSheetScheme } from "../../../validators/exercise-sheet-validator"
import { LoadEmployees } from "../../dashboard-owner-gym/rooms-and-sessions/actions/load-employees"
import { LoadMembers } from "../../dashboard-owner-gym/rooms-and-sessions/actions/load-members"
import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { convertParamsToJson, useCashfitParams } from "@/view/hooks/use-params"
import { ComboSelectWithLabel } from '@/view/components/combo-select-with-label'


interface Params {
  changeTab: (data: string) => void
}

export const FormAddFicha = ({ changeTab }: Params) => {
  const { data: employees } = LoadEmployees()
  const { data: studants } = LoadMembers()
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  const { remoteClientGet } = useRemoteClientGet()
  const { showToast } = useToast()
  const { getUnitId } = useLocalStorage()
  const [params] = useSearchParams()
  const { getParam, setParams } = useCashfitParams(convertParamsToJson(params))
  const sheetId = getParam("sheetId")
  const form = useForm<ExerciseSheetForm>({
    resolver: zodResolver(exerciseSheetScheme),
    defaultValues: {
      nome: "",
      dataInicio: "",
      dataFim: "",
      tipoDuracao: "TREINOS",
      numeroTreinos: 0,
      tipoImpressao: "IMPRESSORA",
      ocultarFichaApp: false,
      fichaModelo: false,
      treinosEfetuados: 0,
      autoAvaliacao: 0,
      professor: { id: "" },
      aluno: { id: "" },
      unidade: { id: getUnitId() },
    },
  })

  const fetchData = async () => {
    if (sheetId) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      remoteClientGet(`/fichas/${sheetId}`, {
        idUnidade: getUnitId(),
      })
        .then((response) => {
          const { data }: any = response.data
          form.setValue("id", data.id + "")
          form.setValue("nome", data.nome)
          form.setValue("dataInicio", data.dataInicio)
          form.setValue("dataFim", data.dataFim)
          form.setValue("tipoDuracao", data.tipoDuracao)
          form.setValue("numeroTreinos", data.numeroTreinos)
          form.setValue("tipoImpressao", data.tipoImpressao)
          form.setValue("ocultarFichaApp", data.ocultarFichaApp)
          form.setValue("fichaModelo", data.fichaModelo)
          form.setValue("treinosEfetuados", data.treinosEfetuados)
          form.setValue("autoAvaliacao", data.autoAvaliacao)
          form.setValue("professor.id", data.professor.id)
          form.setValue("aluno.id", data.aluno.id)
          form.setValue("unidade.id", data.unidade.id)
        })
        .catch(CatchMessageError)
        .finally(() => {
          console.log("Loading data...")
        })
    } else {
      // Reset form when sheetId is not present (e.g., when navigating back)
      form.reset({
        nome: "",
        dataInicio: "",
        dataFim: "",
        tipoDuracao: "TREINOS",
        numeroTreinos: 0,
        tipoImpressao: "IMPRESSORA",
        ocultarFichaApp: false,
        fichaModelo: false,
        treinosEfetuados: 0,
        autoAvaliacao: 0,
        professor: { id: "" },
        aluno: { id: "" },
        unidade: { id: getUnitId() },
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [params])

  const handleSubmit = async (data: ExerciseSheetForm) => {
    try {
      if (!data.dataInicio || !data.dataFim) {
        return showToast("data de inicio ou fim da ficha nao informada", "error")
      }

      if (data?.id) {
        await httpClientPut(`/fichas`, {
          ...data,
          dataInicio: data.dataInicio,
          dataFim: data.dataFim,
          inclusao: new Date(),
        })
        console.log("Ficha editada com sucesso")
        showToast("Ficha editada com sucesso", "success")
      } else {
        await httpClientPost("/fichas", {
          ...data,
          dataInicio: data.dataInicio,
          dataFim: data.dataFim,
        })
        console.log("Ficha criada com sucesso")
        showToast("Ficha criada com sucesso", "success")
      }
      setParams({
        sheetId: '',
      })
      // Update to use the new navigation flow
      changeTab("sheet-list")
      form.reset()
    } catch (error) {
      console.log("Error ao criar ficha", error)
      CatchMessageError(error)
    }
  }

  const handleCancel = () => {
    // Clear form data and navigate back to sheet list
    setParams({
      sheetId: '',
    })
    changeTab("sheet-list")
  }

  const formValues = form.watch()

  useEffect(() => {
    console.log(formValues)
  }, [formValues])

  const {
    formState: { errors },
  } = form

  useEffect(() => {
    console.log("Erros do formulário:", errors)
  }, [errors])

  return (
    <div className="space-y-6">
      {/* {isDirty && (
        <Alert variant="destructive" className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription>
            You have unsaved changes. Please save your form or cancel to discard changes.
          </AlertDescription>
        </Alert>
      )} */}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="grid grid-cols-2 gap-4">
          <div className="grid grid-cols-2 gap-4 col-span-2">
            <FormField
              control={form.control}
              name="aluno.id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Aluno
                  </FormLabel>
                  <FormControl>
                    <ComboSelectWithLabel
                      defaultValue={field.value}
                      data={studants.map((item: any) => ({
                        label: item.nome,
                        value: item.id,
                        ...item,
                      }))}
                      placeholder='Selecione um aluno'
                      onChange={(e) => {
                        console.log(e)
                        field.onChange(e?.value)
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.aluno?.id?.message?.toString()}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="professor.id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Professor
                  </FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value?.toString() || ""}>
                      <SelectTrigger className="">
                        <SelectValue placeholder="Selecione" className="w-full" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees?.map((item: any) => {
                          return (
                            <SelectItem value={item.id.toString()} className="" key={item.id}>
                              <div className="flex gap-2">{item.nome}</div>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="text-xs">
                    {form.formState.errors.professor?.id?.message?.toString()}
                  </FormMessage>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span> Nome da ficha
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="numeroTreinos"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400"></span> Quantidade de treinos
                </FormLabel>
                <FormControl>
                  <NumericFormat
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    prefix=""
                    decimalScale={0}
                    getInputRef={field.ref}
                    {...field}
                    fixedDecimalScale={true}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.numeroTreinos?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="autoAvaliacao"
            render={({ field }) => (
              <FormItem className='hidden'>
                <FormLabel>
                  <span className="text-gray-400"></span> Auto avaliação
                </FormLabel>
                <FormControl>
                  <NumericFormat
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    prefix=""
                    decimalScale={0}
                    getInputRef={field.ref}
                    {...field}
                    fixedDecimalScale={true}
                    allowNegative={false}
                    allowLeadingZeros={false}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.autoAvaliacao?.message}</FormMessage>
              </FormItem>
            )}
          />
          <div className="col-span-2">
            <FormField
              control={form.control}
              name="dataInicio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Data de inicio e fim
                  </FormLabel>
                  <FormControl>
                    <DatePickerWithRange
                      setInitDate={field.onChange}
                      setFinishDate={(value) => {
                        form.setValue("dataFim", value)
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.dataInicio?.message}</FormMessage>
                </FormItem>
              )}
            />
          </div>

          <div className="col-span-2 flex gap-4 justify-end mt-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-acelerafit-green-400 text-muted font-bold hover:bg-acelerafit-green-300"
            >
              Salvar
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
